import React from "react";
import { Clock, Calendar } from "lucide-react";
import { CATEGORY_META, TYPE_LABELS } from "@/lib/constants";
import { toYoutubeEmbed } from "@/lib/media";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function Block({ b }) {
  if (b.type === "subtitle") return <h2>{b.text}</h2>;
  if (b.type === "quote") return <blockquote className="border-l-4 border-nx-orange pl-5 my-8 font-serif text-xl md:text-2xl italic text-nx-dark leading-snug">{b.text}</blockquote>;
  if (b.type === "image" && b.url) return (
    <figure className="my-8"><img src={b.url} alt={b.alt || ""} className="w-full rounded-md" />{(b.caption || b.credit) && <figcaption className="mt-2 text-xs text-gray-500 italic">{b.caption}{b.credit ? ` · ${b.credit}` : ""}</figcaption>}</figure>
  );
  if (b.type === "video") { const e = toYoutubeEmbed(b.url); return e ? <div className="my-8 aspect-video rounded-md overflow-hidden bg-black"><iframe className="w-full h-full" src={e} title="v" allowFullScreen /></div> : null; }
  return <p>{b.text}</p>;
}

// Renders the article exactly as it would appear on the public site, from live editor form state.
export default function ArticlePreview({ open, onOpenChange, form }) {
  const meta = CATEGORY_META[form.category];
  const mainEmbed = toYoutubeEmbed(form.video);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="article-preview-modal">
        <DialogHeader className="p-4 border-b sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2 text-sm">Vista previa <span className="text-[10px] font-bold uppercase bg-nx-violet/10 text-nx-violet px-2 py-0.5 rounded">Borrador · no publicado</span></DialogTitle>
          <DialogDescription>Así se verá la nota en el sitio público de NEXODUM.</DialogDescription>
        </DialogHeader>
        <div className="px-6 sm:px-10 py-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] font-bold tracking-widest uppercase text-white px-2 py-1" style={{ backgroundColor: meta?.color }}>{meta?.label}</span>
            <span className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{TYPE_LABELS[form.type]}</span>
            {form.breaking && <span className="text-[10px] font-bold tracking-widest uppercase bg-red-600 text-white px-2 py-1">Última hora</span>}
          </div>
          <h1 className="font-serif font-black text-3xl md:text-5xl leading-[1.08] text-nx-dark">{form.title || "Título de la nota"}</h1>
          {form.subtitle && <p className="mt-4 text-lg md:text-xl text-gray-600 leading-relaxed font-light">{form.subtitle}</p>}
          <div className="mt-5 flex items-center gap-3 pb-6 border-b border-gray-200">
            {form.author_avatar && <img src={form.author_avatar} alt={form.author} className="w-11 h-11 rounded-full object-cover" />}
            <div className="text-sm">
              <p className="font-bold text-nx-dark">{form.author || "Autor"}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500"><span className="flex items-center gap-1"><Calendar size={12} />Hoy</span><span className="flex items-center gap-1"><Clock size={12} />Lectura estimada</span></div>
            </div>
          </div>

          <figure className="my-8">
            {mainEmbed ? <div className="aspect-video rounded-md overflow-hidden bg-black"><iframe className="w-full h-full" src={mainEmbed} title="main" allowFullScreen /></div>
              : form.image ? <img src={form.image} alt={form.image_alt || form.title} className="w-full rounded-md" /> : <div className="w-full aspect-video bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-sm">Sin imagen principal</div>}
            {form.image_caption && <figcaption className="mt-2 text-xs text-gray-500 italic">{form.image_caption}{form.image_credit ? ` · ${form.image_credit}` : ""}</figcaption>}
          </figure>

          <div className="nx-article-body">
            {(form.content || []).map((b, i) => <Block key={i} b={b} />)}
          </div>

          {form.gallery && form.gallery.filter((g) => g.url).length > 0 && (
            <div className="my-8"><h3 className="font-bold text-nx-dark mb-3">Galería</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {form.gallery.filter((g) => g.url).map((g, i) => (
                  <figure key={i}><img src={g.url} alt={g.alt || ""} className="w-full rounded-md aspect-video object-cover" />{(g.caption || g.credit) && <figcaption className="mt-1.5 text-xs text-gray-500 italic">{g.caption}{g.credit ? ` · ${g.credit}` : ""}</figcaption>}</figure>
                ))}
              </div>
            </div>
          )}

          {form.tags && form.tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap my-8 pt-6 border-t border-gray-200">
              {form.tags.map((t) => <span key={t} className="text-xs bg-nx-soft border border-gray-200 rounded-full px-3 py-1 text-gray-600">#{t}</span>)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
